// Copyright (c) Ergoss Logiciels - All rights reserved - Proprietary and confidential
import { NgTemplateOutlet } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { IonHeader, IonToolbar, IonIcon, IonToggle } from '@ionic/angular/standalone';
import { addIcons } from 'ionicons';
import { chevronBack, chevronDown, arrowForward, checkmark } from 'ionicons/icons';
import { FaIconLibrary, FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import {
  faArrowRight,
  faCalendar,
  faCheck,
  faCircleQuestion,
  faChevronDown,
  faChevronLeft,
  faClock,
  faFilter,
  faPlane,
  faPlaneArrival,
  faPlaneDeparture,
  faStar,
  faTag,
  faTriangleExclamation,
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import { AppViews } from '@shared/models/views';
import { ReportFiltersComponent } from '@features/reports/components/report-filters/report-filters.component';
import { DataStore } from '@core/store/data.store';
import { RootStore } from '@core/store/root.store';
import { FlightsStore } from '@features/flights/store/flights.store';
import type { ListFilterKey } from '@features/flights/store/withFlightFilters';
import { FlightFilterKey } from '@features/flights/store/withFlightFilters';
import { monthOptionLabel } from '@shared/models/flights/month-filter';
import { TourAnchorDirective } from '@ergoss/guided-tour';
import { ToursStore } from '@ergoss/guided-tour';
import { FeatureFlagsStore } from '@core/store/feature-flags.store';
import { UiOverridesStore } from '@core/store/ui-overrides.store';
import { BriefingStore } from '@features/briefing/store/briefing.store';
import { ReportsStore } from '@features/reports/store/reports.store';
import { ColorKey } from '@shared/ui/helpers';
import { TransKey } from 'src/translations';
import { SaraIconComponent } from '@shared/ui/sara-icon/sara-icon.component';
import { ConnectivityStatusComponent } from '@core/layout/connectivity-status/connectivity-status.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'top-bar',
  templateUrl: './top-bar.component.html',
  styleUrls: ['./top-bar.component.scss'],
  imports: [
    NgTemplateOutlet,
    FormsModule,
    IonHeader,
    IonToolbar,
    IonIcon,
    IonToggle,
    SaraIconComponent,
    ConnectivityStatusComponent,
    MatTooltipModule,
    MatMenuModule,
    MatButtonModule,
    FontAwesomeModule,
    ReportFiltersComponent,
    TourAnchorDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopBarComponent {
  readonly ds = inject(DataStore);
  readonly rs = inject(RootStore);
  readonly fs = inject(FlightsStore);
  readonly bs = inject(BriefingStore);
  readonly reportsStore = inject(ReportsStore);
  readonly toursStore = inject(ToursStore);
  readonly ffs = inject(FeatureFlagsStore);
  /** Dev UI overrides — the reminder pill shows while any is in force. */
  readonly uio = inject(UiOverridesStore);

  colorKey = ColorKey;
  transKey = TransKey;
  appViews = AppViews;

  // Flights header controls — scope/filter state lives in FlightsStore (fs);
  // only the filter-key enum + list-picker row config are presentational here.
  readonly filterKey = FlightFilterKey;
  readonly listFilters: readonly { key: ListFilterKey; label: string; placeholder: string; maxLength: number }[] = [
    { key: FlightFilterKey.origin, label: 'Origin', placeholder: 'ICAO', maxLength: 4 },
    { key: FlightFilterKey.destination, label: 'Destination', placeholder: 'ICAO', maxLength: 4 },
    { key: FlightFilterKey.registration, label: 'Registration', placeholder: 'Select', maxLength: 8 },
  ];

  /** Card-colour legend popover (flights header) — pure UI toggle, no store
   *  state needed. Closing the Filters dropdown first keeps the two popovers
   *  from overlapping. */
  readonly legendOpen = signal(false);

  /** Month filter pill label — the draft month's display label ("December
   *  2025"), or the "any" default when no month is staged. */
  readonly monthFilterLabel = computed(() => monthOptionLabel(this.fs.currentFilters().month) || 'Any month');

  /** Tour the "?" button replays — the current view's tour, except the
   *  briefing view whose loaded state carries its own tour (the two states
   *  never render together; each has its own walkthrough + seen-state). The
   *  flight-details tour anchors on the full-screen overlay; a flight embedded
   *  by the split view is still the Flights page. */
  readonly currentTourId = computed(() => {
    if (this.fs.detailOverlayShowing()) return 'flight-details';
    if (this.rs.currentView() === AppViews.briefing && this.bs.showBriefing()) return 'briefing-details';
    return this.rs.currentView();
  });

  constructor() {
    addIcons({ chevronBack, chevronDown, arrowForward, checkmark });
    inject(FaIconLibrary).addIcons(
      faChevronLeft,
      faChevronDown,
      faArrowRight,
      faPlane,
      faPlaneDeparture,
      faPlaneArrival,
      faUser,
      faCalendar,
      faClock,
      faFilter,
      faStar,
      faTag,
      faTriangleExclamation,
      faCheck,
      faCircleQuestion,
    );
  }

  toggleLegend(): void {
    if (this.fs.filtersOpen()) this.fs.closeFilters();
    this.legendOpen.update((v) => !v);
  }
  closeLegend(): void {
    this.legendOpen.set(false);
  }
}
